import type { Conversation } from "../../params/types";

export const GEAR_ICON =
  "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z";
export const PAPERCLIP_ICON =
  "M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48";
export const MIC_ICON =
  "M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z M19 10v2a7 7 0 01-14 0v-2 M12 19v4 M8 23h8";

export function InlineIcon({
  d,
  size = 18,
  strokeWidth = 1.8,
}: {
  d: string;
  size?: number;
  strokeWidth?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

/** Paper-plane send icon (matches Insta-DM Messages). */
export function SendIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

/** Profile URL shown as the link text. Clicks stay on the link. */
export function IgProfileLink({ url }: { url?: string | null }) {
  if (!url) return null;
  return (
    <a
      className="ib-ig-link"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {url}
    </a>
  );
}

export function ChevronIcon({
  size = 16,
  expanded,
}: {
  size?: number;
  expanded?: boolean;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: expanded ? "rotate(180deg)" : "none" }}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export function formatMsgTime(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function conversationPeerKey(c?: Conversation | null) {
  const peer = String(c?.peer_username || "")
    .replace(/^@/, "")
    .trim()
    .toLowerCase();
  if (peer) return peer;
  const id = String(c?.thread_id || "");
  if (id.startsWith("peer:")) return id.slice(5).toLowerCase();
  const title = String(c?.title || "")
    .replace(/^@/, "")
    .trim()
    .split(/[\s\n,·|]/)[0]
    .toLowerCase();
  if (title && /^[a-z0-9._]+$/.test(title)) return title;
  return "";
}

/** Collapse peer:username + real thread rows for the same contact (client-side). */
export function dedupeConversations(
  list?: Conversation[] | null,
): Conversation[] {
  const rows = Array.isArray(list) ? list : [];
  const byPeer = new Map<string, Conversation>();
  const noPeer: Conversation[] = [];
  const score = (c: Conversation) => {
    const real = String(c?.thread_id || "").startsWith("peer:") ? 0 : 1_000_000;
    const msgs = Array.isArray(c?.messages) ? c.messages.length : 0;
    const at =
      new Date(c?.last_message_at || c?.updated_at || 0).getTime() || 0;
    const follower = c?.is_follower_only ? -1000 : 0;
    return real + msgs * 10 + Math.floor(at / 1000) + follower;
  };
  for (const c of rows) {
    const peer = conversationPeerKey(c);
    if (!peer) {
      noPeer.push(c);
      continue;
    }
    const prev = byPeer.get(peer);
    if (!prev || score(c) >= score(prev)) byPeer.set(peer, c);
  }
  return [...byPeer.values(), ...noPeer];
}

export function resolveThreadIdForPeer(
  conversations: Conversation[] | null | undefined,
  peer: string,
  preferredThreadId: string | null = null,
): string | null {
  const peerKey = String(peer || "")
    .replace(/^@/, "")
    .trim()
    .toLowerCase();
  if (preferredThreadId && !String(preferredThreadId).startsWith("peer:")) {
    return preferredThreadId;
  }
  if (!peerKey) return preferredThreadId;
  const real = (conversations || []).find((c) => {
    if (String(c.thread_id || "").startsWith("peer:")) return false;
    return conversationPeerKey(c) === peerKey;
  });
  if (real?.thread_id) return real.thread_id;
  if (preferredThreadId) return preferredThreadId;
  return `peer:${peerKey}`;
}

export function isMediaPlaceholder(text?: string | null) {
  return /^\[(photo|video|media|voice)(:[^\]]*)?\]$/i.test(
    String(text || "").trim(),
  );
}

export function isVoiceMessage({
  text,
  mediaKind,
}: {
  text?: string | null;
  mediaKind?: string | null;
} = {}) {
  const kind = String(mediaKind || "").toLowerCase();
  if (kind === "audio" || kind === "voice") return true;
  return /^\[voice\]/i.test(String(text || "").trim());
}

export function formatThreadPreview(text?: string | null) {
  const t = String(text || "").trim();
  if (!t) return "—";
  if (/^\[voice\]/i.test(t)) return "Voice note";
  if (/^\[photo\]/i.test(t)) return "Photo";
  if (/^\[video\]/i.test(t)) return "Video";
  if (/^\[media\]/i.test(t)) return "Media";
  return t;
}

export function sourceLabel(source?: string | null) {
  if (
    !source ||
    source === "scanned" ||
    source === "bot" ||
    source === "admin"
  ) {
    return "";
  }
  if (source === "queued" || source === "sending") return " · sending…";
  if (source === "scheduled") return " · scheduled";
  if (source === "paused") return " · paused";
  return ` · ${source}`;
}

export function isScheduledBubble(source?: string | null) {
  return source === "scheduled" || source === "paused";
}
